import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  serverTimestamp,
  updateDoc,
} from '../firebase.config';
import { enforcedSetDoc as setDoc } from '../utils/enforcedTracking';
import { useEnhancedCompany } from '../contexts/EnhancedCompanyContext';
import { getCollectionForCompany } from '../utils/dataAdapter';
import { useNotification } from '../contexts/NotificationContext';
import { useCompanyCurrency } from '../hooks/useCompanyCurrency';
import { getCurrencySymbol } from '../utils/currencyUtils';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from './shared/AppSelect';
import {
  normalizeTagName,
  tagDocIdFromName,
  RATE_BASIS_OPTIONS,
  getRateBasisShortLabel,
  normalizeVendorServiceTags,
  isPartyServiceVendor,
  getVendorsForServiceTag,
  resolvePreferredVendors,
  normalizePreferredVendorIds,
  buildPreferredVendorPatch,
  togglePreferredVendorId,
  isPreferredVendorForTag,
  mergeLegacyNamesForRename,
  getTagAliasNames,
  dedupeServiceTagsForDisplay,
  resolveCanonicalServiceTag,
  findDuplicateTagsForName,
  mergeServiceTagFields,
  getTagMatchKey,
} from '../utils/serviceTagRegistry';
import LoadingScreen from './LoadingScreen';
import { Star, Tag, Users, Wrench, Search, Plus, ChevronRight, Pencil, X, Check } from 'lucide-react';

const emptyEditDraft = () => ({
  name: '',
  defaultRate: '',
  defaultCustomerCharge: '',
  rateBasis: 'qty',
  notes: '',
});

const ServiceTags = ({ db, userId, isAuthReady, appId }) => {
  const { currentCompany } = useEnhancedCompany();
  const companyId = currentCompany?.companyId || currentCompany?.id || null;
  const companyCurrency = useCompanyCurrency();
  const currencySymbol = getCurrencySymbol(companyCurrency);
  const { success, error: notifyError } = useNotification();

  const [loading, setLoading] = useState(true);
  const [serviceTags, setServiceTags] = useState([]);
  const [parties, setParties] = useState([]);
  const [newTagName, setNewTagName] = useState('');
  const [newDefaultRate, setNewDefaultRate] = useState('');
  const [newDefaultCustomerCharge, setNewDefaultCustomerCharge] = useState('');
  const [newRateBasis, setNewRateBasis] = useState('qty');
  const [savingTagId, setSavingTagId] = useState('');
  const [selectedTagId, setSelectedTagId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [isEditingTag, setIsEditingTag] = useState(false);
  const [editDraft, setEditDraft] = useState(emptyEditDraft);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const duplicateMergeStartedRef = useRef(false);

  useEffect(() => {
    duplicateMergeStartedRef.current = false;
  }, [companyId]);

  useEffect(() => {
    if (!db || !userId || !isAuthReady || !companyId) {
      setLoading(false);
      return undefined;
    }
    const tagsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'serviceTags');
    const partiesRef = getCollectionForCompany(db, { companyId, appId, userId }, 'parties');
    if (!tagsRef) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const unsubs = [
      onSnapshot(
        tagsRef,
        (snap) => {
          const list = [];
          snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
          setServiceTags(list);
          setLoading(false);
        },
        (err) => {
          console.error('serviceTags listener', err);
          setLoading(false);
        }
      ),
    ];
    if (partiesRef) {
      unsubs.push(
        onSnapshot(
          partiesRef,
          (snap) => setParties(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
          (err) => console.error('parties listener', err)
        )
      );
    }
    return () => unsubs.forEach((u) => u());
  }, [db, userId, isAuthReady, appId, companyId]);

  const sortedTags = useMemo(() => dedupeServiceTagsForDisplay(serviceTags), [serviceTags]);

  const filteredTags = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return sortedTags.filter((t) => {
      const active = t.isActive !== false;
      if (!showInactive && !active) return false;
      if (!q) return true;
      const label = normalizeTagName(t.normalizedName || t.name);
      return label.includes(q);
    });
  }, [sortedTags, searchQuery, showInactive]);

  const selectedTag = useMemo(
    () => filteredTags.find((t) => t.id === selectedTagId) || filteredTags[0] || null,
    [filteredTags, selectedTagId]
  );

  useEffect(() => {
    if (filteredTags.length === 0) {
      setSelectedTagId(null);
      return;
    }
    if (!selectedTagId || !filteredTags.some((t) => t.id === selectedTagId)) {
      setSelectedTagId(filteredTags[0].id);
    }
  }, [filteredTags, selectedTagId]);

  useEffect(() => {
    if (!selectedTagId || !serviceTags.length) return;
    const raw = serviceTags.find((t) => t.id === selectedTagId);
    if (!raw) return;
    const canon = resolveCanonicalServiceTag(serviceTags, raw);
    if (canon?.id && canon.id !== selectedTagId) {
      setSelectedTagId(canon.id);
    }
  }, [serviceTags, selectedTagId]);

  useEffect(() => {
    setIsEditingTag(false);
    if (!selectedTag) {
      setEditDraft(emptyEditDraft());
      return;
    }
    setEditDraft({
      name: normalizeTagName(selectedTag.normalizedName || selectedTag.name),
      defaultRate: selectedTag.defaultRate != null ? String(selectedTag.defaultRate) : '',
      defaultCustomerCharge:
        selectedTag.defaultCustomerCharge != null ? String(selectedTag.defaultCustomerCharge) : '',
      rateBasis: selectedTag.rateBasis || 'qty',
      notes: selectedTag.notes || '',
    });
  }, [selectedTag?.id]);

  const vendorParties = useMemo(
    () =>
      parties
        .filter((p) => isPartyServiceVendor(p))
        .sort((a, b) =>
          String(a.firmName || '').localeCompare(String(b.firmName || ''))
        ),
    [parties]
  );

  const stats = useMemo(() => {
    const active = sortedTags.filter((t) => t.isActive !== false).length;
    const withRate = sortedTags.filter((t) => parseFloat(t.defaultRate) > 0).length;
    const withPreferred = sortedTags.filter((t) => normalizePreferredVendorIds(t).length > 0).length;
    return { active, withRate, withPreferred, vendors: vendorParties.length };
  }, [sortedTags, vendorParties.length]);

  const getTagsCollection = useCallback(() => {
    if (!db || !companyId) return null;
    return getCollectionForCompany(db, { companyId, appId, userId }, 'serviceTags');
  }, [db, companyId, appId, userId]);

  const consolidateDuplicateTags = useCallback(
    async (canonical, patch = {}) => {
      if (!canonical?.id || !db || !companyId) return canonical;
      const dupes = findDuplicateTagsForName(serviceTags, canonical);
      if (!dupes.length) return canonical;
      const col = getTagsCollection();
      if (!col) return canonical;
      let merged = mergeServiceTagFields(canonical, patch);
      dupes.forEach((d) => {
        merged = mergeServiceTagFields(merged, d);
      });
      const ref = doc(col, canonical.id);
      await setDoc(
        ref,
        {
          ...merged,
          ...buildPreferredVendorPatch(normalizePreferredVendorIds(merged), parties),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      await Promise.all(
        dupes.map((d) =>
          setDoc(
            doc(col, d.id),
            { isActive: false, mergedInto: canonical.id, updatedAt: serverTimestamp() },
            { merge: true }
          )
        )
      );
      return { ...merged, id: canonical.id };
    },
    [db, companyId, serviceTags, parties, getTagsCollection]
  );

  useEffect(() => {
    if (loading || !serviceTags.length || duplicateMergeStartedRef.current) return;
    const dedupedCount = dedupeServiceTagsForDisplay(serviceTags).length;
    if (serviceTags.length <= dedupedCount) return;
    duplicateMergeStartedRef.current = true;
    const deduped = dedupeServiceTagsForDisplay(serviceTags);
    void (async () => {
      for (const canon of deduped) {
        await consolidateDuplicateTags(canon);
      }
    })();
  }, [serviceTags, loading, consolidateDuplicateTags]);

  const upsertTag = useCallback(
    async (rawName, fields = {}) => {
      const normalized = normalizeTagName(rawName);
      if (!normalized || !db || !companyId) return;
      const canonical = resolveCanonicalServiceTag(serviceTags, normalized);
      const id =
        canonical?.id && serviceTags.some((t) => t.id === canonical.id)
          ? canonical.id
          : tagDocIdFromName(normalized);
      const col = getTagsCollection();
      if (!col) return;
      const ref = doc(col, id);
      const payload = {
        normalizedName: normalized,
        name: normalized,
        isActive: true,
        source: 'service-tags-page',
        updatedAt: serverTimestamp(),
        ...fields,
      };
      if (fields.defaultRate !== undefined) {
        payload.defaultRate = parseFloat(fields.defaultRate) || 0;
      }
      if (fields.defaultCustomerCharge !== undefined) {
        payload.defaultCustomerCharge = parseFloat(fields.defaultCustomerCharge) || 0;
      }
      if (fields.rateBasis) payload.rateBasis = fields.rateBasis;
      await setDoc(ref, payload, { merge: true });
    },
    [db, companyId, getTagsCollection, serviceTags]
  );

  const persistTagDoc = async (tagDoc, patch, { silent = false } = {}) => {
    if (!db || !companyId || !tagDoc) return null;
    const canonical = resolveCanonicalServiceTag(serviceTags, tagDoc);
    const label = getTagMatchKey(canonical);
    if (!label) return null;
    const col = getTagsCollection();
    if (!col) return null;
    const ref = doc(col, canonical.id);
    setSavingTagId(canonical.id);
    try {
      await setDoc(
        ref,
        {
          normalizedName: label,
          name: label,
          isActive: canonical.isActive !== false,
          ...patch,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      await consolidateDuplicateTags({ ...canonical, ...patch }, patch);
      if (!silent) success('Tag saved.');
      return ref.id;
    } catch (err) {
      console.error('persistTagDoc', err);
      notifyError(err?.message || 'Could not save tag.');
      return null;
    } finally {
      setSavingTagId('');
    }
  };

  const saveTagFields = async (tagDoc, patch) => {
    await persistTagDoc(tagDoc, patch, { silent: true });
  };

  const savePreferredVendors = async (tagDoc, partyIds) => {
    if (!tagDoc) return;
    await persistTagDoc(tagDoc, buildPreferredVendorPatch(partyIds, parties), { silent: true });
  };

  const toggleFavoriteVendor = async (tagDoc, party, label) => {
    if (!tagDoc || !party?.id || !db || !companyId) return;
    try {
      const partiesRef = getCollectionForCompany(db, { companyId, appId, userId }, 'parties');
      if (!partiesRef) return;

      let partyTags = normalizeVendorServiceTags(party.vendorServiceTags);
      if (!partyTags.includes(label)) {
        partyTags = [...partyTags, label];
        await updateDoc(doc(partiesRef, party.id), {
          isServiceVendor: true,
          vendorServiceTags: partyTags,
          updatedAt: serverTimestamp(),
        });
      }

      const col = getTagsCollection();
      const canonical = resolveCanonicalServiceTag(serviceTags, tagDoc);
      const tagRef = doc(col, canonical.id);
      const tagSnap = await getDoc(tagRef);
      const tagData = tagSnap.exists() ? tagSnap.data() : canonical;
      const dupes = findDuplicateTagsForName(serviceTags, canonical);
      let mergedTag = mergeServiceTagFields(
        { ...canonical, ...tagData, id: canonical.id },
        dupes.reduce((acc, d) => mergeServiceTagFields(acc, d), {})
      );
      const nextIds = togglePreferredVendorId(normalizePreferredVendorIds(mergedTag), party.id);
      const added = nextIds.includes(party.id);

      await setDoc(
        tagRef,
        {
          normalizedName: label,
          name: label,
          isActive: true,
          defaultRate: mergedTag.defaultRate,
          rateBasis: mergedTag.rateBasis,
          defaultCustomerCharge: mergedTag.defaultCustomerCharge,
          ...buildPreferredVendorPatch(nextIds, parties),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      await consolidateDuplicateTags(
        { ...mergedTag, preferredVendorIds: nextIds },
        buildPreferredVendorPatch(nextIds, parties)
      );

      success(
        added
          ? `${party.firmName} added to favorites for "${label}".`
          : `${party.firmName} removed from favorites.`
      );
    } catch (err) {
      console.error('toggleFavoriteVendor', err);
      notifyError(err?.message || 'Could not update favorite vendor.');
    }
  };

  const handleAddTag = async (e) => {
    e.preventDefault();
    const raw = newTagName.trim();
    if (!raw) return;
    try {
      await upsertTag(raw, {
        defaultRate: parseFloat(newDefaultRate) || 0,
        defaultCustomerCharge: parseFloat(newDefaultCustomerCharge) || 0,
        rateBasis: newRateBasis,
      });
      const id =
        resolveCanonicalServiceTag(serviceTags, raw)?.id || tagDocIdFromName(normalizeTagName(raw));
      setNewTagName('');
      setNewDefaultRate('');
      setNewDefaultCustomerCharge('');
      setNewRateBasis('qty');
      success('Service tag added.');
      setSelectedTagId(id);
    } catch (err) {
      console.error(err);
      notifyError('Could not add tag.');
    }
  };

  const togglePartyTag = async (party, tagDoc, checked) => {
    if (!db || !companyId || !tagDoc) return;
    const label = normalizeTagName(tagDoc.normalizedName || tagDoc.name);
    const existing = normalizeVendorServiceTags(party.vendorServiceTags);
    const next = checked
      ? Array.from(new Set([...existing, label]))
      : existing.filter((t) => t !== label);
    try {
      const partiesRef = getCollectionForCompany(db, { companyId, appId, userId }, 'parties');
      await updateDoc(doc(partiesRef, party.id), {
        isServiceVendor: true,
        vendorServiceTags: next,
        updatedAt: serverTimestamp(),
      });
      if (!checked) {
        const favIds = normalizePreferredVendorIds(tagDoc).filter((id) => id !== party.id);
        await savePreferredVendors(tagDoc, favIds);
      }
      success(checked ? `${party.firmName} can perform "${label}".` : `${party.firmName} unassigned.`);
    } catch (err) {
      console.error(err);
      notifyError('Could not update vendor assignment.');
    }
  };

  const toggleTagActive = async (tagDoc) => {
    const active = tagDoc.isActive !== false;
    await persistTagDoc(tagDoc, { isActive: !active });
  };

  const saveTagEdit = async () => {
    if (!selectedTag || !db || !companyId) return;
    const newName = normalizeTagName(editDraft.name);
    if (!newName) {
      notifyError('Tag name is required.');
      return;
    }
    const oldName = normalizeTagName(selectedTag.normalizedName || selectedTag.name);
    const fields = {
      defaultRate: parseFloat(editDraft.defaultRate) || 0,
      defaultCustomerCharge: parseFloat(editDraft.defaultCustomerCharge) || 0,
      rateBasis: editDraft.rateBasis || 'qty',
      notes: String(editDraft.notes || '').trim(),
      normalizedName: newName,
      name: newName,
    };
    const preferredIds = normalizePreferredVendorIds(selectedTag);

    setIsSavingEdit(true);
    try {
      const col = getTagsCollection();
      if (!col) return;

      if (newName === oldName || tagDocIdFromName(newName) === selectedTag.id) {
        await saveTagFields(selectedTag, {
          ...fields,
          ...buildPreferredVendorPatch(preferredIds, parties),
        });
        success('Tag updated.');
        setIsEditingTag(false);
        return;
      }

      const newId = tagDocIdFromName(newName);
      const existing = serviceTags.find((t) => t.id === newId && t.isActive !== false);
      if (existing && existing.id !== selectedTag.id) {
        notifyError(`Tag "${newName}" already exists.`);
        return;
      }

      const legacyNames = mergeLegacyNamesForRename(selectedTag, oldName, newName);

      await setDoc(
        doc(col, newId),
        {
          ...selectedTag,
          ...fields,
          ...buildPreferredVendorPatch(preferredIds, parties),
          isActive: true,
          renamedFrom: oldName,
          legacyNames,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      await persistTagDoc(selectedTag, { isActive: false, renamedTo: newName }, { silent: true });

      const partiesRef = getCollectionForCompany(db, { companyId, appId, userId }, 'parties');
      if (partiesRef) {
        const partiesSnap = await getDocs(partiesRef);
        await Promise.all(
          partiesSnap.docs
            .filter((d) => {
              const tags = normalizeVendorServiceTags(d.data().vendorServiceTags);
              return tags.includes(oldName);
            })
            .map((d) => {
              const data = d.data();
              const nextTags = normalizeVendorServiceTags(data.vendorServiceTags).map((t) =>
                t === oldName ? newName : t
              );
              return updateDoc(doc(partiesRef, d.id), {
                vendorServiceTags: nextTags,
                updatedAt: serverTimestamp(),
              });
            })
        );
      }

      setSelectedTagId(newId);
      setIsEditingTag(false);
      success(
        `Tag renamed to "${newName}". Existing bills still show "${oldName}" — rates and favorites apply to both names.`
      );
    } catch (err) {
      console.error(err);
      notifyError('Could not save tag.');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const renderTagDetail = (tag) => {
    if (!tag) return null;
    const label = normalizeTagName(tag.normalizedName || tag.name);
    const active = tag.isActive !== false;
    const assigned = getVendorsForServiceTag(parties, label, tag);
    const preferredList = resolvePreferredVendors(parties, tag);
    const preferredIds = normalizePreferredVendorIds(tag);
    const aliasNames = Array.from(getTagAliasNames(tag)).filter((a) => a !== label);

    return (
      <div className="flex flex-col h-full min-h-0">
        <div className="px-4 sm:px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-indigo-50/80 to-white">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              {!isEditingTag ? (
                <>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className={`inline-flex items-center rounded-lg px-2.5 py-1 text-base font-semibold capitalize ${
                        active ? 'bg-indigo-100 text-indigo-900' : 'bg-slate-100 text-slate-400 line-through'
                      }`}
                    >
                      {label}
                    </span>
                    {!active && (
                      <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                        Inactive
                      </span>
                    )}
                  </div>
                  {preferredList.length > 0 && (
                    <p className="text-sm text-indigo-700 mt-2">
                      <Star className="w-4 h-4 fill-amber-400 text-amber-500 inline mr-1 -mt-0.5" aria-hidden />
                      Favorite vendors ({preferredList.length}):{' '}
                      <strong>{preferredList.map((p) => p.firmName).join(', ')}</strong>
                      <span className="text-indigo-500 font-normal block sm:inline sm:ml-1">
                        — first is auto-selected on bills
                      </span>
                    </p>
                  )}
                  {aliasNames.length > 0 && (
                    <p className="text-xs text-slate-600 mt-2">
                      Also matches on bills:{' '}
                      <span className="font-medium capitalize">{aliasNames.join(', ')}</span>
                      <span className="text-slate-500"> (legacy / renamed)</span>
                    </p>
                  )}
                  {tag.notes && (
                    <p className="text-xs text-slate-500 mt-1 italic">{tag.notes}</p>
                  )}
                </>
              ) : (
                <p className="text-sm font-medium text-indigo-800">Editing tag</p>
              )}
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              {!isEditingTag ? (
                <button
                  type="button"
                  onClick={() => setIsEditingTag(true)}
                  disabled={!active}
                  className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-indigo-200 text-indigo-700 bg-white hover:bg-indigo-50 disabled:opacity-50"
                >
                  <Pencil className="w-4 h-4" />
                  Edit tag
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditingTag(false);
                      setEditDraft({
                        name: label,
                        defaultRate: tag.defaultRate != null ? String(tag.defaultRate) : '',
                        defaultCustomerCharge:
                          tag.defaultCustomerCharge != null ? String(tag.defaultCustomerCharge) : '',
                        rateBasis: tag.rateBasis || 'qty',
                        notes: tag.notes || '',
                      });
                    }}
                    className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={isSavingEdit}
                    onClick={() => void saveTagEdit()}
                    className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                  >
                    <Check className="w-4 h-4" />
                    {isSavingEdit ? 'Saving…' : 'Save changes'}
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={() => void toggleTagActive(tag)}
                className={`text-sm px-3 py-1.5 rounded-lg border ${
                  active
                    ? 'border-red-200 text-red-700 hover:bg-red-50'
                    : 'border-blue-200 text-blue-700 hover:bg-blue-50'
                }`}
              >
                {active ? 'Deactivate' : 'Restore'}
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6">
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
              {isEditingTag ? 'Edit pricing & details' : 'Pricing & cost basis'}
            </h3>
            {isEditingTag ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="sm:col-span-2 block">
                  <span className="text-sm font-medium text-slate-700">Tag name</span>
                  <input
                    type="text"
                    value={editDraft.name}
                    onChange={(e) => setEditDraft((d) => ({ ...d, name: e.target.value }))}
                    className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm capitalize"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Renaming updates party assignments; old bills keep the previous tag text.
                  </p>
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-slate-700">Internal rate ({currencySymbol})</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editDraft.defaultRate}
                    onChange={(e) => setEditDraft((d) => ({ ...d, defaultRate: e.target.value }))}
                    className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-slate-700">Customer charge ({currencySymbol})</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editDraft.defaultCustomerCharge}
                    onChange={(e) => setEditDraft((d) => ({ ...d, defaultCustomerCharge: e.target.value }))}
                    className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm"
                    placeholder="Optional"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-slate-700">Rate ×</span>
                  <AppSelect
                    value={editDraft.rateBasis}
                    onChange={(e) => setEditDraft((d) => ({ ...d, rateBasis: e.target.value }))}
                    aria-label="Rate basis"
                    sheetTitle="Rate basis"
                    options={RATE_BASIS_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                    triggerClassName={`${APP_SELECT_FILTER_TRIGGER} mt-1 rounded-lg py-2.5`}
                  />
                </label>
                <label className="sm:col-span-2 block">
                  <span className="text-sm font-medium text-slate-700">Notes (optional)</span>
                  <textarea
                    value={editDraft.notes}
                    onChange={(e) => setEditDraft((d) => ({ ...d, notes: e.target.value }))}
                    rows={2}
                    className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm resize-y"
                    placeholder="Internal note for your team"
                  />
                </label>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3">
                  <p className="text-xs text-slate-500">Internal rate</p>
                  <p className="text-lg font-semibold text-slate-900">
                    {parseFloat(tag.defaultRate) > 0 ? `${currencySymbol}${tag.defaultRate}` : '—'}
                  </p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3">
                  <p className="text-xs text-slate-500">Customer charge</p>
                  <p className="text-lg font-semibold text-slate-900">
                    {parseFloat(tag.defaultCustomerCharge) > 0
                      ? `${currencySymbol}${tag.defaultCustomerCharge}`
                      : '—'}
                  </p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3">
                  <p className="text-xs text-slate-500">Rate basis</p>
                  <p className="text-lg font-semibold text-slate-900">
                    {getRateBasisShortLabel(tag.rateBasis || 'qty')}
                  </p>
                </div>
              </div>
            )}
          </section>

          <section>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Vendors for this tag
              </h3>
              <span className="text-xs text-slate-500">
                {assigned.length} assigned · {preferredIds.length} favorite{preferredIds.length !== 1 ? 's' : ''}
              </span>
            </div>
            <p className="text-sm text-slate-600 mb-4 leading-relaxed">
              <strong>Checkbox</strong> = vendor can do this work. <strong>Star</strong> = favorite (multiple allowed; first = primary on bills). Clicking star also assigns the vendor if needed.
            </p>
            {vendorParties.length === 0 ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                No service vendors yet. In{' '}
                <Link to="/parties" className="font-medium text-blue-700 underline">
                  Parties
                </Link>
                , set type Seller/Both and enable &quot;Service vendor&quot;.
              </div>
            ) : (
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {vendorParties.map((party) => {
                  const partyTags = normalizeVendorServiceTags(party.vendorServiceTags);
                  const checked = partyTags.includes(label);
                  const isFavorite = isPreferredVendorForTag(tag, party.id);
                  const favRank = preferredIds.indexOf(party.id);
                  return (
                    <li
                      key={party.id}
                      className={`flex items-center gap-2 rounded-xl border p-3 transition-colors ${
                        checked
                          ? isFavorite
                            ? 'border-amber-200 bg-amber-50/40'
                            : 'border-indigo-200 bg-indigo-50/50'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={!active || isEditingTag}
                        onChange={(e) => void togglePartyTag(party, tag, e.target.checked)}
                        className="h-4 w-4 rounded border-slate-300 text-indigo-600 shrink-0"
                        aria-label={`Assign ${party.firmName}`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{party.firmName}</p>
                        <p className="text-xs text-slate-500">
                          {party.partyType}
                          {isFavorite && favRank === 0 && ' · Primary favorite'}
                          {isFavorite && favRank > 0 && ` · Favorite #${favRank + 1}`}
                        </p>
                      </div>
                      <button
                        type="button"
                        title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                        disabled={!active || isEditingTag}
                        onClick={() => void toggleFavoriteVendor(tag, party, label)}
                        className={`p-2 rounded-lg shrink-0 transition-colors ${
                          isFavorite
                            ? 'bg-amber-100 text-amber-700'
                            : 'text-slate-400 hover:bg-slate-100 hover:text-amber-600'
                        }`}
                      >
                        <Star
                          className={`w-5 h-5 ${isFavorite ? 'fill-amber-500 text-amber-500' : ''}`}
                        />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
      </div>
    );
  };

  if (!companyId) {
    return (
      <div className="p-8 text-center text-slate-600">
        Select a company to manage service tags.
      </div>
    );
  }

  if (loading) {
    return <LoadingScreen message="Loading service tags..." />;
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
                <Tag className="w-8 h-8 text-indigo-600" aria-hidden />
                Service tags
              </h1>
              <p className="mt-2 text-sm text-slate-600 max-w-xl leading-relaxed">
                Tags like pasting or framing drive internal costs, vendor defaults, and Service Operations filters.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <Link
                to="/service-operations"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-indigo-700 bg-white border border-indigo-200 rounded-lg hover:bg-indigo-50 shadow-sm"
              >
                <Wrench className="w-4 h-4" />
                Service Operations
              </Link>
              <Link
                to="/parties"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 shadow-sm"
              >
                <Users className="w-4 h-4" />
                Parties
              </Link>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Active tags', value: stats.active, icon: Tag },
              { label: 'With rates', value: stats.withRate, icon: null },
              { label: 'Default vendors set', value: stats.withPreferred, icon: Star },
              { label: 'Service vendors', value: stats.vendors, icon: Users },
            ].map((s) => (
              <div
                key={s.label}
                className="bg-white rounded-xl border border-slate-200 px-4 py-3 shadow-sm"
              >
                <p className="text-xs font-medium text-slate-500">{s.label}</p>
                <p className="text-2xl font-semibold text-slate-900 mt-0.5">{s.value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Add tag */}
        <form
          onSubmit={handleAddTag}
          className="mb-6 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden"
        >
          <div className="px-4 sm:px-5 py-3 border-b border-slate-100 bg-slate-50/80 flex items-center gap-2">
            <Plus className="w-5 h-5 text-indigo-600" />
            <h2 className="text-sm font-semibold text-slate-800">New service tag</h2>
          </div>
          <div className="p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
            <label className="lg:col-span-2 block">
              <span className="text-xs font-medium text-slate-600">Tag name</span>
              <input
                type="text"
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                placeholder="e.g. pasting, framing"
                className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm capitalize"
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Internal rate ({currencySymbol})</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={newDefaultRate}
                onChange={(e) => setNewDefaultRate(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Customer charge ({currencySymbol})</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={newDefaultCustomerCharge}
                onChange={(e) => setNewDefaultCustomerCharge(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm"
                placeholder="Optional"
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Rate ×</span>
              <AppSelect
                value={newRateBasis}
                onChange={(e) => setNewRateBasis(e.target.value)}
                aria-label="Rate basis"
                sheetTitle="Rate basis"
                options={RATE_BASIS_OPTIONS.map((o) => ({
                  value: o.value,
                  label: getRateBasisShortLabel(o.value),
                }))}
                triggerClassName={`${APP_SELECT_FILTER_TRIGGER} mt-1 rounded-lg py-2.5`}
              />
            </label>
          </div>
          <div className="px-4 sm:px-5 pb-4">
            <button
              type="submit"
              disabled={!newTagName.trim()}
              className="w-full sm:w-auto px-5 py-2.5 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add tag
            </button>
          </div>
        </form>

        {/* Master-detail */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(240px,300px)_1fr] gap-4 lg:gap-6 min-h-[420px]">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col overflow-hidden lg:max-h-[calc(100vh-12rem)]">
            <div className="p-3 border-b border-slate-100 space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search tags…"
                  className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg"
                />
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-600 px-1">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  className="rounded border-slate-300"
                />
                Show inactive
              </label>
            </div>
            <ul className="flex-1 overflow-y-auto divide-y divide-slate-100">
              {filteredTags.length === 0 ? (
                <li className="p-6 text-center text-sm text-slate-500">No tags match your search.</li>
              ) : (
                filteredTags.map((tag) => {
                  const label = normalizeTagName(tag.normalizedName || tag.name);
                  const isSelected = selectedTag?.id === tag.id;
                  const active = tag.isActive !== false;
                  const favCount = normalizePreferredVendorIds(tag).length;
                  return (
                    <li key={tag.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedTagId(tag.id)}
                        className={`w-full text-left px-4 py-3 flex items-center gap-2 transition-colors ${
                          isSelected ? 'bg-indigo-50 border-l-4 border-l-indigo-600' : 'hover:bg-slate-50 border-l-4 border-l-transparent'
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <span
                            className={`block font-medium capitalize truncate ${
                              active ? 'text-slate-900' : 'text-slate-400 line-through'
                            }`}
                          >
                            {label}
                          </span>
                          <span className="text-xs text-slate-500">
                            {parseFloat(tag.defaultRate) > 0
                              ? `${currencySymbol}${tag.defaultRate} · ${getRateBasisShortLabel(tag.rateBasis)}`
                              : 'No rate'}
                            {favCount > 0 && ` · ★ ${favCount} fav`}
                          </span>
                        </div>
                        <ChevronRight
                          className={`w-4 h-4 shrink-0 ${isSelected ? 'text-indigo-600' : 'text-slate-300'}`}
                        />
                      </button>
                    </li>
                  );
                })
              )}
            </ul>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col overflow-hidden min-h-[320px] lg:max-h-[calc(100vh-12rem)]">
            {selectedTag ? (
              renderTagDetail(selectedTag)
            ) : (
              <div className="flex flex-col items-center justify-center flex-1 p-8 text-center text-slate-500">
                <Tag className="w-12 h-12 text-slate-300 mb-3" />
                <p className="text-sm">Add a tag above or pick one from the list.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceTags;
